import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Purpose
// -------
// Keep a floating action panel visually attached to the currently selected
// paragraph during the review workflow. This is used on multiple review pages
// (CI, SSI, PAD, etc.) that share a common UX: a scrollable document on the
// left with paragraphs that can be edited/approved, and a floating action
// panel that must stay next to the active paragraph.
//
// Why a hook?
// -----------
// - Centralises the positioning maths and event wiring (scroll, resize,
//   ResizeObserver) so every page doesn't re-implement it.
// - Ensures consistent behavior and fewer focus bugs across modules.
//
// Focus preservation strategy
// ---------------------------
// Updating React state on every keystroke can cause the contentEditable to
// re-render and lose focus. To avoid this, the hook updates panel styles
// directly on the DOM node during editing. When not editing, it updates React
// state for initial/fallback positioning.
export function useFloatingActionPanel({
  containerRef,
  selectedCode,
  isEditing,
  topOffset = -15,
  rightOffset = 0,
  getParagraphEl
}) {
  const panelRef = useRef(null);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0 });

  const resolveParagraphEl = useCallback((code) => {
    if (!code) return null;
    if (typeof getParagraphEl === 'function') return getParagraphEl(code);
    return document.querySelector(`[data-paragraph-container][id="${code}"]`);
  }, [getParagraphEl]);

  const updatePosition = useCallback((codeToUse = selectedCode) => {
    // Compute panel position relative to the scroll container and the active
    // paragraph. We measure the DOM rects to know where the paragraph sits
    // visually, and we account for the container's scroll offset.
    const activeParagraph = resolveParagraphEl(codeToUse);
    if (activeParagraph && containerRef?.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const paragraphRect = activeParagraph.getBoundingClientRect();

      const top = paragraphRect.bottom - containerRect.top + containerRef.current.scrollTop + topOffset;
      const left = paragraphRect.right - containerRect.left + rightOffset;

      if (panelRef.current) {
        // Direct style mutation avoids React re-renders while typing
        panelRef.current.style.top = `${top}px`;
        panelRef.current.style.left = `${left}px`;
      }

      if (!isEditing) {
        // When not editing, synchronise into React state so the initial render
        // or any non-editing updates have the correct position.
        setPanelPosition({ top, left });
      }
    }
  }, [containerRef, rightOffset, resolveParagraphEl, selectedCode, topOffset, isEditing]);

  const updatePositionRaf = useMemo(() => {
    // rAF throttle prevents excessive layout thrash during rapid inputs or
    // scroll/resize storms.
    let frame = 0;
    return (code) => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        updatePosition(code);
      });
    };
  }, [updatePosition]);

  const safeUpdatePosition = useCallback((code) => {
    // Single, pre-guarded public API for callers; always defined
    updatePositionRaf(code);
  }, [updatePositionRaf]);

  useEffect(() => {
    const container = containerRef?.current;
    let resizeObserver = null;

    const attachObserver = () => {
      // Track height/width changes of the active paragraph so the panel moves
      // with content growth/shrink while editing.
      if (!selectedCode) return;
      const activeParagraph = resolveParagraphEl(selectedCode);
      if (!activeParagraph || typeof ResizeObserver === 'undefined') return;
      resizeObserver = new ResizeObserver(() => {
        updatePositionRaf(selectedCode);
      });
      resizeObserver.observe(activeParagraph);
    };

    // Keep the panel anchored during scroll and window resizes.
    const handleScroll = () => updatePositionRaf();
    const handleWindowResize = () => updatePositionRaf();

    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
    }
    window.addEventListener('resize', handleWindowResize, { passive: true });

    attachObserver();
    // Snap once on mount/selection change in case geometry already changed.
    updatePositionRaf();

    return () => {
      if (container) container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleWindowResize);
      if (resizeObserver) {
        try { resizeObserver.disconnect(); } catch (_) {}
        resizeObserver = null;
      }
    };
  }, [containerRef, selectedCode, isEditing, resolveParagraphEl, updatePositionRaf]);

  return { panelRef, panelPosition, updatePositionRaf, safeUpdatePosition };
}


